/**
 * Frontend utility to construct proper image URLs
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * Construct a proper image URL from a photo path
 * Handles: 'filename.jpg', 'public/uploads/filename.jpg', '/public/uploads/filename.jpg', '/uploads/filename.jpg'
 * @param {string} photoPath - Path from backend
 * @returns {string|null} - Full URL like http://localhost:3000/uploads/filename.jpg
 */
export const getProfilePhotoUrl = (photoPath) => {
  if (!photoPath || photoPath.trim() === '') {
    return null;
  }

  // If it's already a full URL, return it
  if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
    return photoPath;
  }

  // Extract just the filename from any path
  // This handles: 'file.jpg', 'path/to/file.jpg', '/public/uploads/file.jpg', etc.
  const filename = photoPath.split('/').pop();

  if (!filename || filename.trim() === '') {
    return null;
  }

  // Always return in standard format: http://localhost:3000/uploads/filename
  return `${API_BASE_URL}/uploads/${filename}`;
};

/**
 * Debug function to log image URL information
 * @param {string} photoPath - Path from backend
 */
export const debugImageUrl = (photoPath) => {
  const url = getProfilePhotoUrl(photoPath);
  console.log('[ImageDebug]', {
    originalPath: photoPath,
    constructedUrl: url,
    apiBaseUrl: API_BASE_URL
  });
  return url;
};

/**
 * Test if an image URL is valid/accessible
 * @param {string} url - Image URL to test
 * @returns {Promise<boolean>} - True if image exists
 */
export const testImageUrl = async (url) => {
  if (!url) return false;
  try {
    const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
    return response.ok || response.status === 0; // 0 for no-cors requests
  } catch {
    return false;
  }
};

export default {
  getProfilePhotoUrl,
  testImageUrl,
  debugImageUrl
};
